<template>
    <base-notification-card
        v-bind="$attrs"
        :class="{ 'clickable-card': !isEntityOwner }"
        @card-clicked="handleCardClicked">
        <template #actions>
            <template v-if="requestStatus === 'open'">
                <pendo-multiselect
                    class="permission-select"
                    :value="selectedPermission"
                    :options="permissionOptionsArray"
                    :append-to-body="false"
                    @click.stop
                    @input="onSelect" />
                <div class="button-group">
                    <pendo-button
                        type="primary"
                        size="mini"
                        label="Accept"
                        :loading="acceptLoading"
                        @click="handleAccept" />
                    <pendo-button
                        type="secondary"
                        size="mini"
                        label="Decline"
                        :loading="declineLoading"
                        @click="handleDecline" />
                    <pendo-button
                        type="subtle"
                        size="mini"
                        label="Manage access"
                        @click="handleManage" />
                </div>
            </template>
            <template v-else-if="isEntityOwner">
                <div class="response-container">
                    <!-- eslint-disable vue/no-v-html -->
                    <span
                        class="response-status"
                        v-html="sanitizedBody" />
                    <!-- eslint-enable vue/no-v-html -->
                    <pendo-button
                        type="subtle"
                        size="mini"
                        label="Manage access"
                        @click="handleManage" />
                </div>
            </template>
            <template v-else>
                <div class="response-container">
                    <!-- eslint-disable vue/no-v-html -->
                    <span
                        class="response-status"
                        v-html="sanitizedBody" />
                    <!-- eslint-enable vue/no-v-html -->
                </div>
            </template>
        </template>
    </base-notification-card>
</template>

<script>
import { PendoButton, PendoMultiselect, PendoNotification } from '@pendo/components';
import BaseNotificationCard from './base-notification-card.vue';
import { processAccessRequestDecision } from '@/common/request-access/request-access.utils';

export default {
    name: 'RequestNotificationCard',
    components: {
        BaseNotificationCard,
        PendoButton,
        PendoMultiselect,
    },
    inheritAttrs: false,
    props: {
        requestedEntityType: {
            type: String,
            default: '',
        },
        requestedPermission: {
            type: String,
            default: 'view',
        },
        requestStatus: {
            type: String,
            default: 'open',
        },
        requestedEntityId: {
            type: String,
            required: true,
        },
        requestId: {
            type: String,
            required: true,
        },
        entityName: {
            type: String,
            required: true,
        },
    },
    emits: ['markAsRead', 'cardClicked', 'manageAccess'],
    data () {
        return {
            selectedPermission: {
                id: this.requestedPermission || 'view',
                label: this.requestedPermission === 'edit' ? 'Editor' : 'Viewer',
            },
            permissionOptionsArray: [
                { label: 'Viewer', id: 'view' },
                { label: 'Editor', id: 'edit' },
            ],
            acceptLoading: false,
            declineLoading: false,
        };
    },
    computed: {
        isEntityOwner () {
            return this.$attrs.id.includes('owner');
        },
        sanitizedBody () {
            return this.$sanitize(this.$attrs.body);
        },
    },
    methods: {
        onSelect (value) {
            this.selectedPermission = value;
        },
        async handleAccept () {
            this.acceptLoading = true;

            try {
                let mappedPermission = this.selectedPermission.id;
                if (this.selectedPermission.id === 'viewer') {
                    mappedPermission = 'view';
                } else if (this.selectedPermission.id === 'editor') {
                    mappedPermission = 'edit';
                }

                await processAccessRequestDecision(this.requestId, 'approve', mappedPermission);

                PendoNotification({
                    type: 'success',
                    title: 'Access request approved',
                    message: `You approved access to ${this.entityName}`,
                    duration: 4000,
                });

                window.pendo.track('RequestAccess_Owner_NotificationCenter', {
                    location: this.requestedEntityType,
                    action: 'accept',
                    permissionRequested: this.requestedPermission,
                    permissionGranted: this.selectedPermission.id,
                });
            } catch {
                PendoNotification({
                    type: 'error',
                    title: 'Error approving request',
                    message: 'Failed to approve access request. Please try again.',
                    duration: 4000,
                });
            } finally {
                this.acceptLoading = false;
            }
        },
        async handleDecline () {
            this.declineLoading = true;

            try {
                await processAccessRequestDecision(this.requestId, 'decline');

                PendoNotification({
                    type: 'success',
                    title: 'Access request declined',
                    message: `You declined access to ${this.entityName}`,
                    duration: 4000,
                });

                window.pendo.track('RequestAccess_Owner_NotificationCenter', {
                    location: this.requestedEntityType,
                    action: 'decline',
                    permissionRequested: this.requestedPermission,
                });
            } catch {
                PendoNotification({
                    type: 'error',
                    title: 'Error declining request',
                    message: 'Failed to decline access request. Please try again.',
                    duration: 4000,
                });
            } finally {
                this.declineLoading = false;
            }
        },
        handleManage () {
            this.$emit('manageAccess', {
                entityType: this.requestedEntityType,
                entityId: this.requestedEntityId,
                notificationId: this.$attrs.id,
            });
        },
        handleCardClicked () {
            // Only emit cardClicked if not the entity owner
            if (!this.isEntityOwner) {
                this.$emit('cardClicked', this.$attrs.id);
            }
        },
    },
};
</script>

<style scoped lang="less">
.permission-select {
    width: 120px;
    margin-bottom: 8px;

    :deep(.multiselect__tags) {
        min-height: 32px;
        padding: 4px 8px;
    }
}

.button-group {
    display: flex;
    gap: -4px;
    align-items: center;
}

.response-container {
    display: flex;
    gap: 8px;
    align-items: center;

    .response-status {
        color: var(--palette-gray-70);
        font-size: 14px;
    }
}
</style>
